import { ENTER, SEMICOLON } from '@angular/cdk/keycodes';
import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatOption,
} from '@angular/material/autocomplete';
import { MatChipGrid, MatChipInput, MatChipRow } from '@angular/material/chips';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faQuestionCircle,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { provideValueAccessor } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { HelpTooltipComponent } from '../../cross-team-shared/tooltips/help-tooltip.component';
import { AbstractFormControl } from '../forms/basic-inputs/abstract-form-control';

@Component({
  selector: 'h3-autocomplete-chips',
  templateUrl: './autocomplete-chips-input.component.html',
  styleUrls: ['./autocomplete-chips-input.component.scss'],
  providers: [provideValueAccessor(AutocompleteChipsComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormField,
    MatLabel,
    MatChipGrid,
    MatChipRow,
    NgForOf,
    AsyncPipe,
    FaIconComponent,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocomplete,
    MatOption,
    MatLabel,
    MatFormField,
    NgIf,
    HelpTooltipComponent,
  ],
})
export class AutocompleteChipsComponent extends AbstractFormControl<string[]> {
  @Input() label = '';
  @Input() autoCompleteValueOnEnter = true;
  @Input() allowDuplicatedValue = true;
  @Input() excludedValues: string[] = [];
  @Input() availableValuesOnly = true;
  @Input({ required: true }) allowedValues!: string[];
  @Input() disabled = false;
  @Output() emittedValues = new EventEmitter<string[]>();
  @ViewChild('chipsInput') private inputRef!: ElementRef;
  @ViewChild(MatAutocompleteTrigger)
  private autoCompleteTrigger!: MatAutocompleteTrigger;

  formControl = new UntypedFormControl();
  valuesForAutocomplete$: Observable<string[]>;
  selectedValues$ = new BehaviorSubject<string[]>([]);
  separatorKeyCodes = [ENTER, SEMICOLON];
  faQuestionCircle = faQuestionCircle;
  faTimesCircle = faTimesCircle;
  showWarning$ = new BehaviorSubject(false);
  warning$ = new BehaviorSubject('');

  constructor(injector: Injector) {
    super(injector);
    this.valuesForAutocomplete$ = combineLatest([
      this.getInput$('allowedValues'),
      this.formControl.valueChanges.pipe(startWith('')),
    ]).pipe(
      map(([availableValues, filterText]) =>
        this.getAutocompleteValues(availableValues, filterText),
      ),
    );
  }

  override handleIncomingValue(value: string[]) {
    this.selectedValues$.next(value);
    this.showWarning$.next(false);
  }

  onOptionSelected(value: string) {
    this.addValue(value);
    this.clearInput();

    // Re-open the autocomplete panel after selection. setTimeout is
    // needed here because Angular Material will close the panel
    // automatically later in the detection change cycle
    setTimeout(() => this.ensurePanelOpen());
  }

  // Called when the user presses enter without giving focus to the
  // autocomplete. We add the top autocomplete value
  onChipInputTokenEnd(partialValueForAutocomplete: string) {
    // Prevent adding the first available option if no text was entered
    if (partialValueForAutocomplete === '') {
      return;
    }
    const isInBlacklist = this.excludedValues.find(
      (value) => value === partialValueForAutocomplete,
    );
    const valueToAdd = this.getValueToAdd(partialValueForAutocomplete);
    const isAllowedValue = this.allowedValues.find(
      (value) => value === valueToAdd,
    );
    if (isInBlacklist || (this.availableValuesOnly && !isAllowedValue)) {
      this.showWarning$.next(true);
      this.warning$.next(
        `${partialValueForAutocomplete} is not a permitted tag`,
      );
    } else {
      this.showWarning$.next(false);
      this.addValue(valueToAdd);
    }

    this.clearInput();
  }

  ensurePanelOpen() {
    this.autoCompleteTrigger.openPanel();
  }

  removeIndex(index: number) {
    const newValue = this.selectedValues$.value.filter((_v, i) => i !== index);
    this.selectedValues$.next(newValue);
    this.emitOutgoingValue(newValue);
    this.emittedValues.emit(newValue);
  }

  private addValue(value: string) {
    const selectedValues = this.selectedValues$.value;
    const newSelectedValues = this.allowDuplicatedValue
      ? [...selectedValues, value]
      : [...new Set([...selectedValues, value])];
    this.selectedValues$.next(newSelectedValues);
    this.emitOutgoingValue(newSelectedValues);
    this.emittedValues.emit(newSelectedValues);
  }

  private clearInput() {
    this.formControl.setValue('');
    this.inputRef.nativeElement.value = '';
  }

  private getAutocompleteValues(availableValues: string[], filterText: string) {
    return availableValues.filter((value) => value.includes(filterText));
  }

  private getValueToAdd(value: string) {
    return this.autoCompleteValueOnEnter
      ? this.getAutocompleteValues(this.allowedValues, value)[0] || ''
      : value;
  }
}
